import type { segment } from "@/APITypes/Segment.cs";

// Notes, moments and timestamps are kept against a session's full recording.
// The highlights-only stream is the highlight segments back to back, so there
// a recording time sits at its segment's highlightsOnlyStart plus the offset
// into it — or nowhere, when it falls outside every highlight.

/** Where a recording time plays on the current stream, or null if it doesn't. */
export function toPlayerClock(
  recordingMs: number,
  segments: segment[],
  playsHighlightsStream: boolean
): number | null {
  if (!playsHighlightsStream) return recordingMs;
  const hit = segments.find(
    (s) => recordingMs >= s.start && recordingMs < s.end
  );
  if (!hit?.isHighlight) return null;
  return hit.highlightsOnlyStart + (recordingMs - hit.start);
}

/**
 * Where the highlights stream best picks up a recording time: the same moment
 * when it's inside a highlight, otherwise the start of the closest one. Null
 * when the session has no highlights.
 */
export function toNearestHighlight(
  recordingMs: number,
  segments: segment[]
): number | null {
  const exact = toPlayerClock(recordingMs, segments, true);
  if (exact !== null) return exact;
  let nearest: segment | null = null;
  let nearestGap = Infinity;
  for (const s of segments) {
    if (!s.isHighlight) continue;
    const gap =
      recordingMs < s.start ? s.start - recordingMs : recordingMs - s.end;
    if (gap < nearestGap) {
      nearest = s;
      nearestGap = gap;
    }
  }
  return nearest ? nearest.highlightsOnlyStart : null;
}

/** The recording time of a point on the current stream. */
export function toRecordingClock(
  playerMs: number,
  segments: segment[],
  playsHighlightsStream: boolean
): number {
  if (!playsHighlightsStream) return playerMs;
  const hit = segments.find(
    (s) =>
      s.isHighlight &&
      playerMs >= s.highlightsOnlyStart &&
      playerMs < s.highlightsOnlyEnd
  );
  return hit ? hit.start + (playerMs - hit.highlightsOnlyStart) : playerMs;
}
